import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useRegion } from '../context/RegionContext';

/**
 * Breadcrumbs Component
 * Displays navigation breadcrumbs with proper region-aware links
 * 
 * @param {Array} items - Array of breadcrumb items: [{ name: string, url: string }]
 * @param {string} currentPage - Name of the current page (no link)
 */
export const Breadcrumbs = ({ items = [], currentPage }) => {
  const { region, currentRegion } = useRegion();
  
  // Build region-prefixed URL
  const buildUrl = (url) => {
    if (!url) return null;
    if (currentRegion === 'global') return url;
    return `/${currentRegion}${url}`;
  };
  
  // Get region display name for breadcrumbs
  const getRegionBreadcrumb = () => {
    if (currentRegion === 'global') return null;
    return {
      name: region.name,
      url: `/${currentRegion}`
    };
  };
  
  const regionBreadcrumb = getRegionBreadcrumb();
  
  // Build full breadcrumb list
  const allBreadcrumbs = [
    { name: 'Home', url: '/', isHome: true },
    ...(regionBreadcrumb ? [regionBreadcrumb] : []),
    ...items
  ];
  
  return (
    <nav 
      className="bg-gray-50 border-b border-gray-200" 
      aria-label="Breadcrumb"
      data-testid="breadcrumbs"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <ol className="flex items-center flex-wrap gap-1 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
          {allBreadcrumbs.map((crumb, index) => (
            <li 
              key={index} 
              className="flex items-center"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-gray-400 mx-1 flex-shrink-0" aria-hidden="true" />
              )}
              
              {crumb.url ? (
                <Link
                  to={buildUrl(crumb.url)}
                  className="text-gray-500 hover:text-brand-red smooth-transition flex items-center gap-1"
                  itemProp="item"
                  data-testid={`breadcrumb-${crumb.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {crumb.isHome && <Home className="w-4 h-4" />}
                  <span itemProp="name">{crumb.name}</span>
                </Link>
              ) : (
                <span 
                  className="text-gray-700 font-medium truncate max-w-[200px] sm:max-w-none"
                  itemProp="name"
                >
                  {crumb.name}
                </span>
              )}
              <meta itemProp="position" content={index + 1} />
            </li>
          ))}
          
          {/* Current page (no link) */}
          {currentPage && (
            <li 
              className="flex items-center"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <ChevronRight className="w-4 h-4 text-gray-400 mx-1 flex-shrink-0" aria-hidden="true" />
              <span 
                className="text-brand-green font-semibold truncate max-w-[200px] sm:max-w-none"
                itemProp="name"
                aria-current="page"
                data-testid="breadcrumb-current"
              >
                {currentPage}
              </span>
              <meta itemProp="position" content={allBreadcrumbs.length + 1} />
            </li>
          )}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
